import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Heart, Users, Shield, Target, ArrowRight, Sparkles, Zap, CheckCircle, Clock, FileText, Phone, HandshakeIcon, Award, Building2, Network } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const AccompagnementPage = () => {
  const [heroRef, heroInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [processRef, processInView] = useInView();
  const [benefitsRef, benefitsInView] = useInView();

  const services = [
    { icon: Users, title: 'Accompagnement personnalisé', desc: 'Un interlocuteur dédié pour comprendre votre situation et vos besoins.', gradient: 'from-blue-500 to-cyan-500' },
    { icon: FileText, title: 'Montage de dossier', desc: 'Aide à la constitution du dossier et à la collecte des pièces justificatives.', gradient: 'from-green-500 to-emerald-500' },
    { icon: HandshakeIcon, title: 'Médiation', desc: 'Facilitation du dialogue entre les parties en cas de difficulté.', gradient: 'from-purple-500 to-pink-500' },
    { icon: Shield, title: 'Suivi de garantie', desc: 'Accompagnement tout au long de la mise en œuvre de la garantie solidaire.', gradient: 'from-amber-500 to-orange-500' },
  ];

  const steps = [
    { num: '01', title: 'Premier contact', desc: 'Prise de contact et écoute de votre situation.', icon: Phone },
    { num: '02', title: 'Analyse du besoin', desc: 'Évaluation de votre demande et orientation.', icon: Target },
    { num: '03', title: 'Constitution du dossier', desc: 'Aide au montage et vérification des pièces.', icon: FileText },
    { num: '04', title: 'Instruction', desc: 'Transmission à F.O.G.E.D.O.M pour instruction.', icon: Building2 },
    { num: '05', title: 'Décision', desc: 'Examen par FOGEDOM-SCIC et notification.', icon: CheckCircle },
    { num: '06', title: 'Mise en œuvre', desc: 'Accompagnement jusqu\'à résolution.', icon: Award },
  ];

  const benefits = [
    { icon: Clock, title: 'Réactivité', desc: 'Réponse rapide à vos demandes' },
    { icon: Heart, title: 'Humanité', desc: 'Approche bienveillante et à l\'écoute' },
    { icon: Shield, title: 'Expertise', desc: 'Compétences techniques et juridiques' },
    { icon: Network, title: 'Réseau', desc: 'Accès à un écosystème complet' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1920&q=80" 
            alt="Professional guidance and support" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-orange-600/20 to-amber-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/fogedom" label="Retour à FOGEDOM-SCIC" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 text-orange-300 border border-orange-500/30 mb-6 px-4 py-2">
              <Heart className="w-4 h-4 mr-2" /> À vos côtés
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-orange-100 to-white bg-clip-text text-transparent">Accompagnement</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-10 leading-relaxed">
              Un <span className="text-orange-400 font-semibold">accompagnement humain et personnalisé</span> à chaque étape 
              de votre demande de garantie solidaire.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
                Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30 mb-4 px-4 py-2"><Sparkles className="w-4 h-4 mr-2" /> Nos services</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Un accompagnement <span className="text-orange-400">complet</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section ref={processRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-orange-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2"><Zap className="w-4 h-4 mr-2" /> Le parcours</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">6 étapes pour vous <span className="text-purple-400">accompagner</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <div key={index} className={`relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 hover:border-purple-500/50 transition-all duration-300 hover:-translate-y-1 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center border border-purple-500/30">
                    <step.icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <span className="text-3xl font-black text-purple-500/30">{step.num}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-24 bg-gradient-to-br from-orange-600 via-amber-600 to-orange-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Pourquoi nous <span className="text-orange-200">choisir</span> ?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 text-center ${benefitsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-white/70">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Prêt à être <span className="text-orange-400">accompagné</span> ?</h2>
          <p className="text-xl text-slate-400 mb-10">Contactez-nous dès maintenant pour être mis en relation avec un conseiller.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
              Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-slate-600 text-white hover:bg-slate-800 px-10 py-6 text-lg transition-all duration-300" onClick={() => window.location.href = '/adhesion'}>
              Devenir membre
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AccompagnementPage;
